import type { ApiError } from '@ctcells/api'
import type { SampleReport } from '@ctcells/contract'
import { errorMessage } from '@ctcells/i18n'

import type { TrayCellView } from '../../figures/TrayFigure.tsx'

import { bundleSequence, orderBundles } from './bundleCardModel.ts'
import { chamberFill } from './flowVocabulary.ts'
import { TRAY_SLOTS } from './monitoringModel.ts'
import type { SampleBundle } from './patientChambers.ts'
import type { DrawerChambers, DrawerReport } from './MonitoringSampleDrawer.tsx'
import type { SampleFlowView } from './MonitoringScreen.tsx'

/**
 * 모니터링 시료 서랍의 **챔버 구역을 카드 격자와 같은 한 벌에서** 고른다.
 *
 * 🔴 **카드 격자가 그리는 그 묶음(`SampleFlowView.bundles`)에서 그 시료의 카드를 집는다** — 서랍이 시료↔웰
 *    대응을 따로 짓지 않는다(그 대응은 아직 임시다 · `temporarySampleGroups.ts`). 순번도 카드와 같은 함수다.
 * 🔴 카드 격자가 못 봤으면(시료 목록 403 · 조회 중) **그 사유를 그대로** 적는다 — 「챔버 없음」으로 접지 않는다.
 * ⚠ 그 시료가 지금 도는 작업에 없으면 `none` — 그릴 챔버가 없다(값 없는 구역은 세우지 않는다).
 */
export function drawerChambersOf(flow: SampleFlowView, sampleId: string): DrawerChambers {
  if (flow.notice !== undefined) return { kind: 'notice', text: flow.notice }
  const ordered = orderBundles(flow.bundles)
  const bundle = ordered.find((item) => item.placeholder !== true && item.sampleId === sampleId)
  if (bundle === undefined) return { kind: 'none' }
  const seq = bundleSequence(ordered).get(bundle.key)
  return {
    kind: 'bundle',
    bundle,
    batchStages: flow.batchStages ?? [],
    stale: flow.stale === true,
    ...(seq === undefined ? {} : { seq }),
  }
}

/**
 * 「배정 웰」 줄에 적을 **그 시료의 묶음이 갖는 웰 전부** — 챔버 구역과 같은 출처(`bundle.chambers`) · 같은 순서.
 * 🔴 묶음을 못 찾았거나(레시피 없음 · 작업에 없음) 챔버가 비면 `undefined` — 줄은 서버 값 하나로 선다.
 */
export function drawerAssignedWellsOf(chambers: DrawerChambers): readonly string[] | undefined {
  if (chambers.kind !== 'bundle') return undefined
  const wells = chambers.bundle.chambers.map((dot) => dot.well)
  return wells.length === 0 ? undefined : wells
}

/**
 * 진행 · 분주 구역의 상태 — `GET /reports/{id}`(부가 자료).
 * 🔴 **권한이 없어 안 부른 것은 사유가 된다** — 시료 상세도 같은 사유로 막혔어도 그렇다(viewer).
 *    구역마다 제 사유를 적는다(2026-09-28 팀장 지시 「그 구역에만 권한이 없습니다」). 종전엔 겹친다고 `none` 으로
 *    접었는데, 그러면 진행 · 분주 기록 구역이 **통째로 사라져** 「원래 없는 구역」처럼 읽혔다.
 */
export function drawerReportOf(input: {
  locked: boolean
  lockedText: string
  pending: boolean
  error: ApiError | null
  data: SampleReport | undefined
}): DrawerReport {
  if (input.locked) return { kind: 'notice', text: input.lockedText }
  if (input.error !== null) return { kind: 'notice', text: errorMessage(input.error) }
  if (input.data !== undefined) return { kind: 'ready', report: input.data }
  return input.pending ? { kind: 'loading' } : { kind: 'none' }
}

/**
 * 서랍 챔버 구역의 **트레이 한 장**(eric 2026-10-06 「여기 그림에... 챔버 말고.. 트레이를 그려달라고」) — 그 시료의 웰만
 * 챔버가 든 칸으로, 나머지 칸은 빈 자리(점선)로. 칸 차례 · 이름은 랩웨어 규격(`TRAY_SLOTS`)이다.
 * 🔴 실패한 챔버는 판정 표지(✗)로 — 색만으로 말하지 않는다(`TrayFigure`). 대조 묶음은 구역 머리의 「대조」 표가 말한다.
 * 🔴 묶음의 웰이 하나라도 트레이 칸 이름이 아니면 `undefined` — 칸을 지어내지 않고 부르는 쪽이 챔버 그림으로 남긴다.
 */
export function drawerTrayCells(bundle: SampleBundle): TrayCellView[] | undefined {
  const mine = new Map(bundle.chambers.map((dot) => [dot.well, dot]))
  if ([...mine.keys()].some((well) => !TRAY_SLOTS.includes(well))) return undefined
  return TRAY_SLOTS.map((slot) => {
    const dot = mine.get(slot)
    if (dot === undefined) return { slot, present: false }
    /* 실패가 먼저 · 그 밖은 챔버 진행(`ChamberProgress.status` — 카드 아래 한 마디와 같은 값)을 표지로 */
    const verdict =
      chamberFill(dot) === 'fail'
        ? 'fail'
        : dot.progress?.status === 'active' || dot.progress?.status === 'transfer'
          ? 'active'
          : dot.progress?.status === 'done'
            ? 'pass'
            : dot.progress?.status === 'failed'
              ? 'fail'
              : dot.progress?.status === 'pending'
                ? 'pending'
                : undefined
    return { slot, present: true, ...(verdict === undefined ? {} : { verdict }) }
  })
}
